import { Product } from "gql/generated";
import { SmartImage } from "shared/components";
import { getImageDimension, getValueImages } from "shared/merchCatalog";
import { getMultilingualString } from "shared/utils";
import { Box, Flex, styled } from "styled-system/jsx";

const DOT_SIZE = 14;
const MAX_DOTS = 6;

/** Common merch colour names that aren't CSS colour keywords. */
const COLOUR_ALIASES: Record<string, string> = {
  cream: "#F3E9D2",
  natural: "#E8DCC4",
  oatmeal: "#D8CBB0",
  sand: "#D6C49A",
  charcoal: "#36454F",
  heathergrey: "#9AA0A6",
  heathergray: "#9AA0A6",
  burgundy: "#800020",
  forest: "#228B22",
  mustard: "#E1AD01",
  rust: "#B7410E",
  sage: "#9CAF88",
  mint: "#98D8C8",
  denim: "#1560BD",
  camo: "#78866B",
};

const resolveCssColour = (label: string): string | null => {
  const key = label.toLowerCase().replace(/[^a-z]/g, "");
  if (!key) return null;
  if (COLOUR_ALIASES[key]) return COLOUR_ALIASES[key];
  if (typeof CSS !== "undefined" && CSS.supports("color", key)) return key;
  return null;
};

const Dot = styled("span", {
  base: {
    display: "inline-flex",
    flexShrink: 0,
    borderRadius: "full",
    overflow: "hidden",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "border.light",
    transitionDuration: "fast",
    transitionProperty: "transform, border-color",
  },
  variants: {
    active: {
      true: { borderColor: "border.dark", transform: "scale(1.15)" },
    },
  },
});

type ColourDotsProps = {
  product: Product;
  activeValueId: string | null;
  onHoverValue: (valueId: string | null) => void;
};

/** Swatches for the dimension that drives photos; hovering one previews its photo. */
export const ColourDots = ({
  product,
  activeValueId,
  onHoverValue,
}: ColourDotsProps) => {
  const imageDimId = getImageDimension(product)?._id;
  const imageDim = product.merchMetadata?.dimensions?.find(
    d => !!imageDimId && d?._id === imageDimId,
  );
  const values = (imageDim?.values ?? []).filter(
    (v): v is NonNullable<typeof v> & { _id: string } => !!v?._id,
  );
  if (values.length < 2) return null;

  const shown = values.slice(0, MAX_DOTS);
  const overflow = values.length - shown.length;

  return (
    <Flex
      gap="75"
      alignItems="center"
      pt="50"
      aria-hidden
      onMouseLeave={() => onHoverValue(null)}
    >
      {shown.map(value => {
        const englishLabel = getMultilingualString(value.label ?? {}, "en");
        const colour = resolveCssColour(englishLabel);
        const swatchImage = colour ? null : getValueImages(product, value._id)[0];
        return (
          <Dot
            key={value._id}
            active={activeValueId === value._id}
            title={getMultilingualString(value.label ?? {})}
            onMouseEnter={() => onHoverValue(value._id)}
            style={{
              width: DOT_SIZE,
              height: DOT_SIZE,
              background: colour ?? undefined,
            }}
          >
            {swatchImage ? (
              <SmartImage
                image={swatchImage}
                width={DOT_SIZE}
                height={DOT_SIZE}
                shape="circle"
              />
            ) : (
              !colour && <Box w="full" h="full" bg="bg.wa.lightest" />
            )}
          </Dot>
        );
      })}
      {overflow > 0 && (
        <styled.span textStyle="smallMedium" color="text.mid">
          +{overflow}
        </styled.span>
      )}
    </Flex>
  );
};
